<script setup lang="ts">
// DESIGN.md section 9.1, screen 4 — Past matches. Paginated list (date, players, games score,
// format, rating deltas); tap a row for the detail screen.
//
// 2026-08-31 round-6 feedback — rows were ellipsing everything and the score wrapped. Redesigned
// to two fixed lines per row:
//   1. avatar — name (+ elo subtitle) — STANDING (score, protected width, never wraps/ellipses)
//      — name (+ elo subtitle) — avatar
//   2. date, format ("30 ago, Classic 21 · Bo3" — es-ES date, compact)
// The elo subtitle ("1512 (+12)") needs THIS match's before/after per player — already on the
// wire via `Match.ratingChanges` (server/app/services/match_service.py's serialize_match, which
// list_matches already calls per row) — no backend change needed. Left off entirely when that
// match has no rating_history rows (pre-existing matches / abandoned).
import { onMounted, ref, watch } from 'vue'
import Avatar from '../components/Avatar.vue'
import { getMatches } from '../backend'
import { navigate } from '../store/nav'
import { tableStore } from '../store/table'
import type { Match } from '../types'

const PAGE_SIZE = 12

const items = ref<Match[]>([])
const total = ref(0)
const offset = ref(0)
const loading = ref(false)
const loadError = ref(false)

async function load() {
  loading.value = true
  loadError.value = false
  try {
    const res = await getMatches({ status: 'finished', table: tableStore.table, limit: PAGE_SIZE, offset: offset.value })
    items.value = res.items
    total.value = res.total
  } catch {
    loadError.value = true
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(offset, load)

// Compact es-ES date ("30 ago") — line 2 also carries the format, so this stays short on
// purpose; full time-of-day isn't needed here (it's on the detail screen).
function dateLabel(m: Match): string {
  const d = new Date(m.finishedAt ?? m.startedAt)
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
}

// "30 ago, Classic 21 · Bo3" — date, then match type + best-of. Abandoned matches (not currently
// returned by this screen's `status: 'finished'` query, but kept defensive in case that ever
// changes) get an explicit tag instead of silently showing a 0-0-looking score.
function metaLine(m: Match): string {
  const parts = [dateLabel(m), `${m.matchType.name} · Bo${m.bestOf}`]
  if (m.status !== 'finished') parts.push('Abandoned')
  return parts.join(', ')
}

// STANDING — the games-won score, never wrapped/ellipsed (fixed width + whitespace-nowrap in the
// template). "—" for a non-finished row instead of a possibly-misleading 0–0.
function scoreLabel(m: Match): string {
  if (m.status !== 'finished') return '—'
  return `${m.gamesWon[m.players.left.id] ?? 0}–${m.gamesWon[m.players.right.id] ?? 0}`
}

// Lichess-style elo subtitle: this match's resulting rating + the delta that produced it, e.g.
// "1512 (+12)". Empty when this match has no rating_history rows for that player (leave the
// subtitle off rather than fake a number — see file header).
// Round-9 (2026-08-31): color the subtitle by the delta's sign — gains green, losses red.
function eloClass(m: Match, playerId: number): string {
  const delta = m.ratingChanges?.[playerId]?.delta ?? 0
  return delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-neutral-500'
}

// Round-9: winner's avatar gets a highlight ring in the list row.
function isWinner(m: Match, playerId: number): boolean {
  return m.winnerPlayerId === playerId
}

function eloSubtitle(m: Match, playerId: number): string {
  const rc = m.ratingChanges?.[playerId]
  if (!rc) return ''
  const sign = rc.delta >= 0 ? '+' : ''
  return `${rc.after} (${sign}${rc.delta})`
}

function openDetail(m: Match) {
  navigate('pastMatchDetail', { matchId: m.id })
}

function prevPage() {
  offset.value = Math.max(0, offset.value - PAGE_SIZE)
}
function nextPage() {
  if (offset.value + PAGE_SIZE < total.value) offset.value += PAGE_SIZE
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-3">
    <h1 class="text-xl font-bold">Matches</h1>

    <div v-if="loadError" class="flex-1 flex items-center justify-center text-neutral-500">Unavailable</div>
    <div v-else class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
      <div v-if="!loading && items.length === 0" class="text-neutral-500">No finished matches yet</div>
      <button
        v-for="m in items"
        :key="m.id"
        type="button"
        class="tap-target w-full flex flex-col gap-1 rounded-xl bg-neutral-900 border border-neutral-800 px-3 py-2 text-left active:bg-neutral-800"
        @click="openDetail(m)"
      >
        <!-- Line 1: avatar — name/elo — STANDING (protected width) — name/elo — avatar -->
        <div class="flex items-center gap-2">
          <span class="shrink-0 rounded-full" :class="isWinner(m, m.players.left.id) ? 'ring-2 ring-emerald-400' : ''">
            <Avatar :player="m.players.left" :size="36" />
          </span>
          <div class="min-w-0 flex-1 flex flex-col">
            <span class="font-semibold text-sm truncate">{{ m.players.left.displayName }}</span>
            <span v-if="eloSubtitle(m, m.players.left.id)" class="text-[10px] font-mono truncate" :class="eloClass(m, m.players.left.id)">
              {{ eloSubtitle(m, m.players.left.id) }}
            </span>
          </div>

          <span class="shrink-0 whitespace-nowrap min-w-[3.5rem] text-center font-mono font-bold text-neutral-200 text-base px-1">
            {{ scoreLabel(m) }}
          </span>

          <div class="min-w-0 flex-1 flex flex-col items-end text-right">
            <span class="font-semibold text-sm truncate">{{ m.players.right.displayName }}</span>
            <span v-if="eloSubtitle(m, m.players.right.id)" class="text-[10px] font-mono truncate" :class="eloClass(m, m.players.right.id)">
              {{ eloSubtitle(m, m.players.right.id) }}
            </span>
          </div>
          <span class="shrink-0 rounded-full" :class="isWinner(m, m.players.right.id) ? 'ring-2 ring-emerald-400' : ''">
            <Avatar :player="m.players.right" :size="36" />
          </span>
        </div>

        <!-- Line 2: date, format — freed up by moving off line 1, where it used to force the
             ellipsing/wrapping this redesign fixes. -->
        <div class="text-center text-[9px] text-neutral-600/80 font-mono">{{ metaLine(m) }}</div>
      </button>
    </div>

    <div class="flex items-center justify-between">
      <button type="button" class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold disabled:opacity-30" :disabled="offset === 0" @click="prevPage">
        ← Prev
      </button>
      <span class="text-sm text-neutral-500">{{ total === 0 ? '0' : offset + 1 }}–{{ Math.min(offset + PAGE_SIZE, total) }} of {{ total }}</span>
      <button
        type="button"
        class="tap-target px-4 rounded-xl bg-neutral-800 active:bg-neutral-700 font-semibold disabled:opacity-30"
        :disabled="offset + PAGE_SIZE >= total"
        @click="nextPage"
      >
        Next →
      </button>
    </div>
  </div>
</template>
